import React from 'react'

import './_grid.scss'

import cn from '../../libs/class-names'
import {mapChildrenWithFragments} from '../../services/react-children'

import {CommonProps, Overwrite} from '../../types'

import {Spacings} from '../stack/Stack'

import GridCell from './GridCell'

// TODO: get rid of space="none" once ActionBar is reworked and components don't have (bottom) margins themselves anymore
type Props = Overwrite<
  CommonProps<'div'>,
  {
    children: React.ReactNode
    /** Horizontal spacing (responsive behaviour) */
    gap?: '80' | '90' | '100' | '200' | 'none'
    /** Vertical spacing: accepts all CCDS spacing values or inherits gap's value */
    space?: Spacings | 'inherit' | 'none'
    /** Overrides space for tablet screen sizes */
    spaceM?: Spacings
    /** Overrides space for desktop screen sizes */
    spaceL?: Spacings
    align?: 'start' | 'end' | 'center' | 'stretch'
  }
>

/**
 * The prop **gap** defines the size of the column-gap (automatic responsive behaviour see _packages/commons/src/styles/libs/_grid.scss_).
 *
 * The props **space**, **spaceM** and **spaceL** define the size of the row-gap and accept all CCDS spacings values ('60', '70', ..., '1400').
 *
 * Use **spaceM** and **spaceL** to add a responsive behaviour (tablet and desktop) to row-gap.
 *
 * Set **space** to 'inherit' if the row-gap should have the same size as column-gap.
 */
const Grid = ({
  className,
  children,
  gap = '100',
  space = '100',
  spaceM,
  spaceL,
  align = 'start',
  ...props
}: Props) => (
  <div
    {...props}
    className={cn(
      'Grid',
      {
        [`space-${space}`]: !!space,
        [`spaceM-${spaceM}`]: !!spaceM,
        [`spaceL-${spaceL}`]: !!spaceL,
        [`gap-${gap}`]: !!gap,
        [`align-${align}`]: !!align,
      },
      className
    )}
  >
    {mapChildrenWithFragments<React.ComponentProps<typeof GridCell>>(children, (child, key) =>
      React.cloneElement(child, {key, stretch: align === 'stretch'})
    )}
  </div>
)

export default Grid
